import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { NotificationService } from '../../services/notification.service';
import { ViewNotificationDTO, NotificationQueryParams } from '../../models/notification.model';
import { TokenStorageService } from '../../../core/auth/services/token-storage.service';

@Component({
  selector: 'app-notification-list-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, TableModule, ButtonModule, TagModule, DatePipe],
  templateUrl: './notification-list-dialog.component.html',
  styleUrls: ['./notification-list-dialog.component.css']
})
export class NotificationListDialogComponent implements OnInit, OnChanges {
  @Input() display = false;
  @Output() displayChange = new EventEmitter<boolean>();
  @Output() notificationRead = new EventEmitter<ViewNotificationDTO>();

  notifications: ViewNotificationDTO[] = [];
  loading = false;
  totalRecords = 0;
  first = 0;
  rows = 10;
  rowsPerPageOptions = [5, 10, 25, 50];

  private accountId: number | null = null;

  get visible(): boolean {
    return this.display;
  }

  set visible(value: boolean) {
    this.display = value;
    this.displayChange.emit(value);
  }

  constructor(
    private notificationService: NotificationService,
    private tokenStorage: TokenStorageService
  ) {}

  ngOnInit() {
    this.accountId = this.tokenStorage.getAccountId();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['display'] && this.display) {
      // Reset paginazione quando si apre il dialog
      this.first = 0;
      this.loadNotifications();
    }
  }

  loadNotifications(event?: any) {
    if (!this.accountId) {
      console.warn('Account ID non trovato');
      return;
    }

    this.loading = true;

    // Aggiorna paginazione se l'evento è stato passato
    if (event) {
      this.first = event.first;
      this.rows = event.rows;
    }

    const page = Math.floor(this.first / this.rows) + 1;
    const params: NotificationQueryParams = {
      AccountId: this.accountId,
      Page: page,
      PageSize: this.rows
    };

    this.notificationService.getNotifications(params).subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          this.notifications = response.data;
          if (response.meta?.total_items != null) {
            this.totalRecords = response.meta.total_items;
          } else if (response.data.length < this.rows) {
            this.totalRecords = (page - 1) * this.rows + response.data.length;
          } else {
            this.totalRecords = page * this.rows + 1;
          }
        } else {
          console.error('Errore nel caricamento delle notifiche:', response.message);
          this.notifications = [];
          this.totalRecords = 0;
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Errore nel caricamento delle notifiche:', error);
        this.notifications = [];
        this.totalRecords = 0;
        this.loading = false;
      }
    });
  }

  markAsRead(notification: ViewNotificationDTO, event: Event) {
    event.stopPropagation();
    
    if (notification.notification_id === null || notification.notification_is_checked) {
      return;
    }

    const previousState = notification.notification_is_checked;
    notification.notification_is_checked = true;

    this.notificationService.markAsRead(notification.notification_id).subscribe({
      next: (response) => {
        if (response.isSuccess && response.data) {
          const index = this.notifications.findIndex(n => n.notification_id === notification.notification_id);
          if (index !== -1) {
            this.notifications[index] = response.data;
          }
          this.notificationRead.emit(response.data);
        } else {
          notification.notification_is_checked = previousState;
        }
      },
      error: (error) => {
        console.error('Errore nell\'aggiornamento della notifica:', error);
        notification.notification_is_checked = previousState;
      }
    });
  }

  getNotificationDate(dateString: string): Date {
    return new Date(dateString);
  }

  getSeverity(isChecked: boolean): string {
    return isChecked ? 'secondary' : 'info';
  }

  onHide() {
    this.display = false;
    this.displayChange.emit(false);
  }

  onPageChange(event: any) {
    this.loadNotifications(event);
  }
}

